// Desktop runtime (local-first migration, Phase 4).
//
// In the Tauri desktop build on the OB-van mini PC, the live cue path runs
// against the on-prem local agent over localhost (which in turn drives vMix on
// the LAN + Singular). In the browser build there is no agent and no LAN access
// to vMix, so the vMix controls are gated off (view/edit still work).
//
// isDesktop is true only inside the Tauri shell. VITE_FORCE_DESKTOP=true can
// force it on for local development against a manually-run agent.
export const isDesktop =
  (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) ||
  import.meta.env.VITE_FORCE_DESKTOP === 'true';

const AGENT_URL = import.meta.env.VITE_LOCAL_AGENT_URL || 'http://127.0.0.1:4555';

async function post(path, body) {
  const res = await fetch(`${AGENT_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`agent ${path} → ${res.status}`);
  return res.json().catch(() => ({}));
}

// Per-show context (vMix host/port, Singular key, cue set) the agent needs.
// Singular key is fetched from the cloud backend at show open and handed to the
// agent locally — it is never synced to the client store.
export const agent = {
  // `instances` MUST be forwarded — the agent resolves a cue action's
  // target_instance_id against session.vmixInstances. Dropping it here left that
  // map empty, so every targeted command (and inputs fetch) silently fell back
  // to the Primary vMix: the input dropdown looked right but commands never
  // reached the intended instance.
  setSession: (showId, { vmix, singular, cues, instances }) => post('/session', { showId, vmix, singular, cues, instances }),
  execute: (showId, actions, show, cueId) => post('/execute', { showId, actions, show, cueId }),
  cancel: (showId) => post('/cancel', { showId }),
  loopStart: (showId, cue, mode) => post('/loop/start', { showId, cue, mode }),
  loopStopBounded: (cueId) => post('/loop/stop-bounded', { cueId }),
  loopStop: (cueId) => post('/loop/stop', { cueId }),
  loopStopAll: () => post('/loop/stop-all', {}),
  vmixFunction: (showId, fn, input, value) => post('/vmix/function', { showId, function: fn, input, value }),
  vmixConnect: (showId) => post('/vmix/connect', { showId }),
  vmixInputs: async (showId, instanceId) => {
    const q = new URLSearchParams({ showId });
    if (instanceId) q.set('instanceId', instanceId);
    const res = await fetch(`${AGENT_URL}/vmix/inputs?${q}`);
    if (!res.ok) throw new Error(`agent inputs → ${res.status}`);
    return res.json();
  },
  singularShow: (showId, subId) => post('/singular/show', { showId, sub_composition_id: subId }),
  singularHide: (showId, subId) => post('/singular/hide', { showId, sub_composition_id: subId }),
};

/** Subscribe to the agent's event stream (tally + singular status). Returns an
 *  unsubscribe fn. Reconnects on drop while active. */
export function connectAgentEvents(handlers) {
  let ws = null;
  let closed = false;
  const wsUrl = AGENT_URL.replace(/^http/, 'ws') + '/events';

  const open = () => {
    if (closed) return;
    ws = new WebSocket(wsUrl);
    ws.onmessage = (ev) => {
      try {
        const { type, data } = JSON.parse(ev.data);
        if (type === 'vmix:tally_update') handlers.onTally?.(data);
        else if (type === 'singular:composition_status') handlers.onSingularStatus?.(data);
        else if (type === 'rundown:cue_actions_started') handlers.onActionsStart?.(data);
        else if (type === 'rundown:cue_actions_finished') handlers.onActionsEnd?.(data);
      } catch { /* ignore */ }
    };
    ws.onclose = () => { if (!closed) setTimeout(open, 2000); };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  };
  open();

  return () => { closed = true; if (ws) try { ws.close(); } catch { /* ignore */ } };
}
